import ctaMan from "assets/images/psp/inlineCTA.jpg";
import HeaderBackgroundDesktop from "assets/images/psp/hero/electricity-hero.webp";
import HeaderBackgroundMobile from "assets/images/psp/hero/electricity-header-mobile.webp";
import HeaderBackgroundTablet from "assets/images/psp/hero/electricity-header-tablet.webp";
import illustrationSearch from "assets/images/psp/illustration-search.svg";
import GeorgeRansom from "assets/images/psp/review-george.png";
import AnnAlarcon from "assets/images/psp/review-ann.png";
import SMurphy from "assets/images/psp/review-murphy.png";
import ElectricityBenefitsImage from "assets/images/psp/storage-with-electricity-benefits.webp";
import BatteryTenderImage from "assets/images/psp/battery-tender.webp";
import StorageWorkshopImage from "assets/images/psp/storage-workshop.webp";
import StorageRemoteWorkImage from "assets/images/psp/storage-remote-work.webp";
import { storageCategories } from "pages/PSPPage/utils/getPSPLinks";

const faqs = [
	{
		question: "Do storage units have lights inside?",
		answer: "Most basic storage units include minimal lighting; usually just a single bulb or no lighting at all. Storage units with electrical outlets often come with enhanced lighting packages or the ability to install your chosen lighting system. Standard units typically have motion-activated LED lights that provide basic visibility for loading and unloading.\n\nFor detailed work or frequent access, you'll want storage space with electricity that allows you to install workshop-quality lighting. Many powered units include fluorescent or LED strip lighting packages as add-on options. This is especially important if you plan to work on projects in your unit or need to access stored items regularly during evening hours."
	},
	{
		question: "What can I plug into storage units with power outlets?",
		answer: "Storage units with electrical outlets typically support standard 110V devices up to 15-20 amps per circuit. Common items include battery chargers for vehicles and boats, dehumidifiers, small power tools, lighting systems, and electronic equipment requiring standby power. Most facilities allow fans, heaters (where permitted), and basic workshop equipment.\n\nHigher-power items like welding equipment, large air compressors, or industrial tools require storage units with power that include 220V service. Always check with the facility about their electrical capacity and restrictions. Some facilities prohibit certain high-draw equipment or require dedicated circuits for heavy-duty tools.\n\nRV storage with electricity typically includes specialized 30-amp or 50-amp hookups designed specifically for recreational vehicles. These connections support RV electrical systems, allowing you to maintain battery levels, run refrigerators, and keep interior systems functional during storage. Standard wall outlets won't provide adequate power for full RV electrical needs."
	},
	{
		question: "How much extra does self-storage with electricity cost?",
		answer: "Storage space with power typically adds $15-$70 monthly to your base storage cost, depending on the electrical service level and additional amenities. Basic 110V outlet access usually costs $15-$25 extra per month, while climate-controlled units with power range from $45-$70 additional monthly.\n\nSpecialized power services cost more: RV hookups with 30/50-amp service add $25-$45 monthly, while dedicated 220V circuits for workshop equipment can add $35-$65. Many facilities offer package deals combining power, lighting, and climate control that provide better value than individual add-ons."
	},
	{
		question: "Can I run tools and equipment in my powered storage unit?",
		answer: 'Most storage facilities allow basic tool use and equipment operation in storage units with power outlets, but policies vary significantly between facilities. Common permitted activities include battery charging, basic maintenance work, cleaning equipment, and running diagnostic tools. Many facilities encourage these activities as they indicate active, responsible storage use.\n\nRestrictions typically apply to activities that create noise, odors, or safety hazards. Welding, spray painting, and heavy manufacturing work are usually prohibited. Some facilities have specific "workshop hours" or require advance notice for equipment use. Always review the facility\'s policies before assuming you can operate equipment.'
	},
	{
		question: "What types of items need storage units with power?",
		answer: "Items requiring or benefitting from powered storage fall into several categories:\n\n**Vehicles:**\n* Cars and vehicles that are being stored with trickle chargers or battery maintainers\n* RVs, as powered units can preserve batteries and prevent deterioration\n* Boats requiring battery charging in the offseason\n\n**Collectibles:**\n* Fragile or collectible items that require climate control and other environmental monitoring would benefit from storage units with electricity\n* Books\n* Wine\n* Leather goods\n\n**Musical Instruments:**\n* Pianos\n* Guitars with amps\n* Wind instruments\n\n**Electronics:**\n* Computer equipment and audio gear may require additional electrical support in storage to function optimally\n\n**Business Storage Uses:**\n* Business equipment like refrigeration units, security systems, and inventory requiring temperature control requires consistent power."
	}
];

const relatedContentCards = [
	{
		category: "Self Storage",
		url: "https://www.storage.com/blog/storage-units-with-electricity/",
		date: "December 29, 2025",
		image: "https://www.storage.com/blog/wp-content/uploads/2024/10/outlet2-scaled.webp",
		content:
			"Everything You Need to Know About Storage Units with Electricity"
	},
	{
		category: "Recreational Storage",
		url: "https://www.storage.com/blog/what-is-battery-tender-vehicle-storage/",
		date: "August 4, 2025",
		image: BatteryTenderImage,
		content:
			"What Is a Battery Tender? A Complete Guide for Vehicle Storage"
	},
	{
		category: "Recreational Storage",
		url: "https://www.storage.com/blog/can-you-use-storage-unit-as-workshop/",
		date: "November 24, 2025",
		image: StorageWorkshopImage,
		content: "Can You Use a Storage Unit as a Workshop?"
	},
	{
		category: "Self Storage",
		url: "https://www.storage.com/blog/setup-office-in-a-storage-unit/",
		date: "November 24, 2025",
		image: StorageRemoteWorkImage,
		content: "How to Make a Remote Office in a Storage Unit"
	}
];

const pspLinksContent = {
	title: "More Ways to Store",
	image: illustrationSearch,
	storageTypes: storageCategories.electricityStorage
};

const reviewsContent = {
	title: "Storage.com has a 4.4 out of 5 star rating on",
	testimonials: [
		{
			body: 'Great experience with my storage! I love the " drive right in" climate-controlled storage area. Staff was super helpful with telling me about the system they use and setting me up.',
			author: "Jachooz",
			title: "Great experience with my storage",
			imageSrc: GeorgeRansom
		},
		{
			body: "Very knowledgeable and detail-oriented. The storage facility the Storage.com rep recommended is very close to where I live and at a reasonable rate. Climate-controlled and state-of-the-art security, which I can appreciate. Thanks again for all of your help and information.",
			author: "C. Meredith",
			title: "Excellent Customer Service",
			imageSrc: SMurphy
		},
		{
			body: "I had never used a storage unit before and had many questions regarding the necessary size, locations, climate-controlled, etc. [Storage.com](https://storage.com)'s rep made sure all of my questions were answered and the reservation was made.",
			author: "Hilary Martin",
			title: "Excellent help when I needed it!",
			imageSrc: AnnAlarcon
		}
	]
};

const electricityHerodescription = `
You've invested in quality tools, equipment, and vehicles, but traditional storage units can't keep them functional. [Batteries drain](https://storage.com/blog/what-is-battery-tender-vehicle-storage/), you're working in the dark, and equipment sits unused when you could be maintaining or using it.\n\n
Storage units with electricity solve this by providing power outlets, proper lighting, and the ability to run essential equipment. Your stored items stay maintained and accessible – whether you're a [hobbyist working on restoration projects](https://storage.com/blog/woodworking-workshopstorage-guide/), a musician protecting instruments, or a small business needing workshop space.\n\n
Storage.com connects you with powered storage solutions that keep your equipment ready when you are.
`;

export const PSPElectricityContent = {
	type: "Storage Units with Electricity",
	sections: [
		{
			title: "Storage Units with Electricity hero",
			noPadding: true,
			modules: [
				{
					type: "hero-section",
					props: {
						title: "Find the Top Storage Units with Electricity",
						description: electricityHerodescription,
						searchPlaceholder: "Search by city, state, or zip...",
						buttonText: "Storage Units With Electricity",
						heroImages: {
							desktop: HeaderBackgroundDesktop,
							tablet: HeaderBackgroundTablet,
							mobile: HeaderBackgroundMobile
						},
						heroAltText: "Storage Units with Electricity Hero Image"
					}
				}
			]
		},
		{
			title: "Storage with Electricity Benefits",
			noPadding: true,
			modules: [
				{
					type: "video-content",
					props: {
						title: "Why Storage with Electricity Changes Everything",
						description: "",
						additionalContent: true,
						markDown:
							" ### Keep Equipment Functional, Not Just Safe ### \n\nStorage space with electricity transforms standard storage into active protection. Your valuable items need more than shelter – they need power to:\n\n* **Maintain battery systems** in vehicles, boats, and RVs\n* **Run dehumidifiers** to prevent moisture damage in musical instruments and electronics\n* **[Power security systems](https://storage.com/blog/self-storage-security-features/)** for high-value collections\n* **Operate lighting** for safe access and detailed work\n* **Keep diagnostic tools running** for ongoing maintenance projects\n\n ### Workshop-Ready Storage Solutions ### \n\nTurn your storage unit into an extension of your garage, [art studio](https://storage.com/blog/storing-art-supplies/), or workshop. Storage units with power outlets enable:\n\n* **On-site maintenance** without hauling equipment home\n* **Climate control systems** that work effectively\n* **Tool charging stations** for cordless equipment\n* **Lighting systems** that let you work safely at any hour\n* **Electronic monitoring** of stored items' condition\n\n ### Perfect for High-Value Items ### \n\nItems worth storing are often worth powering. Powered storage units protect:\n\n* Classic cars and [motorcycles](https://storage.com/blog/storing-a-motorcycle-self-storage/) with battery maintenance systems\n* Musical instruments requiring stable humidity control\n* Electronics and computer equipment need ventilation\n* Workshop tools and equipment are ready for immediate use\n* RVs require electrical systems to prevent deterioration",
						mediaType: "image",
						mediaUrl: ElectricityBenefitsImage,
						videoTitle: ""
					}
				}
			]
		},
		{
			title: "Storage Amenities with Electricity",
			noPadding: true,
			modules: [
				{
					type: "size-chart",
					props: {
						title: "Supercharge Your Storage Unit with Electricity: Common Amenity Combinations",
						description:
							"If your storage facility allows it, storage units with electricity can turn bare storage space into a blank canvas of productivity. Here's how to create a workspace in a storage unit with electricity, but remember to check with facility management first.",
						background: "white",
						storages: [
							{
								title: "Standard Unit w/ Power",
								bestFor: [
									"Tools",
									"Electronics charging",
									"Small projects"
								]
							},
							{
								title: "XL Unit w/ Power",
								bestFor: [
									"Hobbyists",
									"Small repairs",
									"Seasonal workshop"
								]
							},
							{
								title: "Climate Control",
								bestFor: [
									"Sensitive items",
									"Electronics",
									"Workshop space"
								]
							},
							{
								title: "Security",
								bestFor: [
									"High-end collections",
									"Expensive equipment",
									"Business inventory"
								]
							}
						]
					}
				}
			]
		},
		{
			title: "Storage with Electricity Sizes & Pricing",
			noPadding: true,
			modules: [
				{
					type: "content-section",
					props: {
						title: "Storage Unit with Electricity Sizes & Pricing",
						background: "light-blue",
						listType: "single",
						content: [
							{
								id: "1",
								value: "What [size storage unit](https://www.storage.com/self-storage-size-guide/) with electricity do you need? The short answer: it depends."
							},
							{
								id: "2",
								value: "Looking for the long answer? Explore our breakdown of powered storage unit sizes and their applications to determine the ideal option for your specific needs."
							},
							{
								id: "3",
								value: " ### [5x10](https://storage.com/blog/5x10-storage-unit-size-guide/) Powered Units ### "
							},
							{
								id: "4",
								value: "**Best for:** Workshop tool storage, musical instrument collections, small electronics\n"
							},
							{
								id: "5",
								value: `* Standard outlets for basic power needs\n* Adequate lighting for detailed work\n* Space for workbench setup\n* **Average Monthly Range:** $85-$140 (including power)`
							},
							{
								id: "6",
								value: " ### [10x10](https://storage.com/blog/10x10-storage-unit-size-guide/) Workshop Storage ### \n"
							},
							{
								id: "7",
								value: "\n**Best for:** Classic car projects, extensive tool collections, workshop equipment\n"
							},
							{
								id: "8",
								value: `\n* Multiple outlet locations\n* 220V service available\n* Room for vehicle plus workspace\n* **Average Monthly Range:** $120-$200 (including power)`
							},
							{
								id: "9",
								value: " ### [10x20](https://storage.com/blog/10x20-storage-unit-size-guide/) RV Storage with Power ### \n"
							},
							{
								id: "10",
								value: "\n**Best for:** RV storage with electricity, boat storage, multiple vehicle storage\n"
							},
							{
								id: "11",
								value: "\n* 30/50 amp RV hookups available\n* High-ceiling clearance\n* Drive-up access\n* **Average Monthly Range:** $150-$275 (including power)"
							},
							{
								id: "12",
								value: " ### [10x30](https://storage.com/blog/10x30-storage-unit-size-guide/) Premium Workshop ### \n"
							},
							{
								id: "13",
								value: "\n**Best for:** Professional workshop setups, multiple project storage, business equipment\n"
							},
							{
								id: "14",
								value: "\n* Dedicated electrical service\n* Workshop lighting packages\n* Climate control integration\n* **Average Monthly Range:** $200-$350 (including power)"
							}
						],
						isColumnWise: false
					}
				}
			]
		},
		{
			title: "Storage with Electricity CTA",
			noPadding: true,
			modules: [
				{
					type: "inline-cta",
					props: {
						storageType: "Electricity",
						subheading: "Make a free reservation in minutes!",
						searchPlaceholder: "Search by city, state, or zip...",
						searchButtonText: "Find Storage",
						image: ctaMan,
						imageAltText: "Find storage units with electricity",
						background: "white"
					}
				}
			]
		},
		{
			title: "Customer Reviews",
			noPadding: true,
			modules: [
				{
					type: "reviews",
					props: reviewsContent
				}
			]
		},
		{
			title: "Storage with Electricity FAQs",
			noPadding: true,
			modules: [
				{
					type: "faq",
					props: {
						title: "Storage with Electricity FAQs",
						faqs
					}
				}
			]
		},
		{
			title: "Storage with Electricity Related Content",
			noPadding: true,
			modules: [
				{
					type: "related-content",
					props: {
						heading: "Learn More About Storage with Electricity",
						unitCards: relatedContentCards,
						background: "light-blue"
					}
				}
			]
		},
		{
			title: "Links to Other PSPs",
			noPadding: true,
			modules: [
				{
					type: "psp-links",
					props: {
						storageGridContent: pspLinksContent,
						background: "white"
					}
				}
			]
		},
		{
			title: "Storage Near You",
			noPadding: true,
			modules: [
				{
					type: "nearby-cities",
					props: {
						storageType: "electricity",
						background: "light-blue"
					}
				}
			]
		}
	]
};

export default PSPElectricityContent;
